import { loadPublicBookingData } from "../lib/publicBookingLoad";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ChevronDown, ChevronUp, Clock, CreditCard } from "lucide-react";
import { toast } from "sonner";
import BookingModal from "../components/BookingModal";
import PracticeTracking from "../components/tracking/PracticeTracking";
import { emitTrackingEvent } from "../lib/tracking/runtime";
import GoogleReviewsCarousel from "../components/GoogleReviewsCarousel";
import { publicAPI, publicStudioAPI } from "../lib/api";
import { formatCurrency } from "../lib/utils";
import { resolveDesignTheme } from "../lib/designThemes";
import { erfasseHerkunft } from "../lib/acquisitionTracking";

function formatServicePrice(price, currency) {
  const value = Number(price || 0);
  if (!Number.isFinite(value) || value <= 0) return "Kostenlos";
  return formatCurrency(value, currency || "EUR");
}

function servicePriceStartsAt(service) {
  return Number(service?.price || 0) > 0 && service?.price_starts_at === true;
}

function formatServicePriceLabel(service, currency) {
  const label = formatServicePrice(service?.price, currency);
  return servicePriceStartsAt(service) ? `ab ${label}` : label;
}

function shouldShowServicePrice(service) {
  return service?.show_price !== false;
}

function hasComparePrice(service) {
  const price = Number(service?.price || 0);
  const comparePrice = Number(service?.compare_price || 0);
  return shouldShowServicePrice(service) && Number.isFinite(comparePrice) && comparePrice > price && comparePrice > 0;
}

function normalizePublicRedirectUrl(value) {
  const raw = String(value || "").trim();
  if (!raw || typeof window === "undefined") return "";
  try {
    const parsed = new URL(raw, window.location.origin);
    if (!["http:", "https:"].includes(parsed.protocol)) return "";
    return parsed.toString();
  } catch {
    return "";
  }
}

function getPrimaryCategory(service) {
  if (service?.category_primary_id && service?.category_primary) {
    return {
      id: String(service.category_primary_id),
      name: service.category_primary,
      sort: service.category_primary_sort ?? 9999,
      bannerImageUrl: service.category_primary_banner_image_url || null,
    };
  }

  if (Array.isArray(service?.service_categories) && service.service_categories.length > 0) {
    const first = service.service_categories[0];
    return {
      id: String(first.id || first.name || "uncategorized"),
      name: first.name || "Weitere Leistungen",
      sort: first.sort_order ?? 9999,
      bannerImageUrl: first.banner_image_url || null,
    };
  }

  return {
    id: "uncategorized",
    name: "Weitere Leistungen",
    sort: 9999,
    bannerImageUrl: null,
  };
}

function getServiceCategories(service) {
  if (Array.isArray(service?.service_categories) && service.service_categories.length > 0) {
    return service.service_categories
      .map((category) => ({
        id: String(category.id || category.name || "uncategorized"),
        name: category.name || "Weitere Leistungen",
        sort: category.sort_order ?? 9999,
        bannerImageUrl: category.banner_image_url || null,
      }))
      .sort((a, b) => {
        const sortDiff = Number(a.sort ?? 999999) - Number(b.sort ?? 999999);
        if (sortDiff !== 0) return sortDiff;
        return String(a.name || "").localeCompare(String(b.name || ""), "de");
      });
  }
  return [getPrimaryCategory(service)];
}

function compareServicesBySortOrder(a, b) {
  const sortDiff = Number(a.sort_order ?? 999999) - Number(b.sort_order ?? 999999);
  if (sortDiff !== 0) return sortDiff;
  return String(a.name || "").localeCompare(String(b.name || ""), "de");
}

function buildGroupedServices(services, categories, selectedCategoryId) {
  const categoryMap = new Map(categories.map((category) => [String(category.id), { ...category, services: [] }]));
  services.forEach((service) => {
    getServiceCategories(service).forEach((category) => {
      const groupId = String(category.id);
      if (!categoryMap.has(groupId)) {
        categoryMap.set(groupId, { ...category, services: [] });
      }
      const group = categoryMap.get(groupId);
      if (!group.services.some((entry) => String(entry.id) === String(service.id))) {
        group.services.push(service);
      }
    });
  });
  return Array.from(categoryMap.values())
    .filter((group) => selectedCategoryId === "all" || String(group.id) === String(selectedCategoryId))
    .map((group) => ({ ...group, services: [...group.services].sort(compareServicesBySortOrder) }))
    .filter((group) => group.services.length > 0)
    .sort((a, b) => {
      const sortDiff = Number(a.sort ?? 999999) - Number(b.sort ?? 999999);
      if (sortDiff !== 0) return sortDiff;
      return String(a.name || "").localeCompare(String(b.name || ""), "de");
    });
}

function PublicBookingServiceCard({
  service,
  org,
  theme,
  expandedDescriptions,
  setExpandedDescriptions,
  onSelect,
}) {
  const description = String(service.description || "");
  const expanded = Boolean(expandedDescriptions[service.id]);
  const currency = service.currency || org?.currency || "EUR";
  const servicePrice = Number(service.price || 0);
  const depositAmount = (() => {
    if (!service.deposit_enabled) return 0;
    if (service.deposit_type === "fixed") return Number(service.deposit_value || 0);
    return Math.round(servicePrice * (Number(service.deposit_value || 0) / 100) * 100) / 100;
  })();
  const paymentDueNow = service.deposit_enabled && depositAmount > 0
    ? depositAmount
    : service.payment_type === "online"
      ? servicePrice
      : 0;

  return (
    <button
      type="button"
      onClick={() => onSelect(service)}
      className="w-full text-left bg-white p-5 rounded-2xl shadow-sm hover:shadow-md transition-all h-full"
      data-testid={`service-option-${service.id}`}
    >
      <div className="flex flex-col h-full">
        {service.image_url ? (
          <div className="-mx-5 -mt-5 mb-4 h-36 overflow-hidden rounded-t-2xl bg-[#F5F5F7]">
            <img
              src={service.image_url}
              alt={service.name}
              className="h-full w-full object-cover"
              loading="lazy"
            />
          </div>
        ) : null}
        <div className="flex-1">
          <h3 className="font-semibold text-[#1D1D1F]">{service.name}</h3>
          {description ? (
            <div className="mt-1">
              <p className={`text-sm text-[#86868B] ${expanded ? "" : "line-clamp-3"}`}>
                {description}
              </p>
              {description.length > 140 ? (
                <button
                  type="button"
                  className="mt-2 text-sm font-medium text-[#1D1D1F] underline underline-offset-2"
                  onClick={(event) => {
                    event.stopPropagation();
                    setExpandedDescriptions((previous) => ({
                      ...previous,
                      [service.id]: !previous[service.id],
                    }));
                  }}
                >
                  {expanded ? "Weniger anzeigen" : "Mehr anzeigen"}
                </button>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-3 text-sm text-[#86868B]">
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {service.duration} Min
            </span>
            {paymentDueNow > 0 ? (
              <span className="flex items-center gap-1" style={{ color: theme?.accent || "#34C759" }}>
                <CreditCard className="w-4 h-4" />
                Zahlung direkt bei Buchung: {formatCurrency(paymentDueNow, currency)}
              </span>
            ) : null}
          </div>
          {shouldShowServicePrice(service) ? (
            <span className="text-right">
              {hasComparePrice(service) ? (
                <span className="block text-xs text-[#86868B] line-through">
                  {formatCurrency(service.compare_price, currency)}
                </span>
              ) : null}
              <span className="text-lg font-semibold text-[#1D1D1F]">
                {formatServicePriceLabel(service, currency)}
              </span>
            </span>
          ) : null}
        </div>
      </div>
    </button>
  );
}

export default function PublicBooking() {
  const { orgSlug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const locationIdFromQuery = searchParams.get("location_id");
  const embedded = searchParams.get("embedded") === "1";

  const [loading, setLoading] = useState(true);
  const [org, setOrg] = useState(null);
  const [locations, setLocations] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState(locationIdFromQuery || "");
  const [selectedCategoryId, setSelectedCategoryId] = useState("all");
  const [selectedService, setSelectedService] = useState(null);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [openCategoryIds, setOpenCategoryIds] = useState([]);
  const [paymentNotice, setPaymentNotice] = useState(null);
  const [googleReviews, setGoogleReviews] = useState(null);

  const theme = useMemo(() => {
    return resolveDesignTheme(org || {});
  }, [org]);
  const activeLocationId = locationIdFromQuery || selectedLocationId || "";

  const selectedLocation = useMemo(() => {
    if (!activeLocationId) return null;
    return locations.find((location) => String(location.id) === String(activeLocationId)) || null;
  }, [activeLocationId, locations]);

  const loadSequence = useRef(0);
  const automaticLocation = useRef(null);
  const loadBookingPage = useCallback(async () => {
    // Selecting the sole location must not repeat the just-completed requests.
    const automatic = automaticLocation.current;
    automaticLocation.current = null;
    if (automatic?.orgSlug === orgSlug && automatic.id === activeLocationId) return;
    const sequence = ++loadSequence.current;
    setLoading(true);
    try {
      const data = await loadPublicBookingData(publicAPI, orgSlug, activeLocationId);
      if (sequence !== loadSequence.current) return;
      const locationRows = Array.isArray(data.locations) ? data.locations : [];
      setOrg(data.org || null);
      setLocations(locationRows);
      setServices(Array.isArray(data.services) ? data.services : []);
      if (!locationIdFromQuery && !selectedLocationId && locationRows.length === 1) {
        const id = String(locationRows[0].id);
        automaticLocation.current = { orgSlug, id };
        setSelectedLocationId(id);
      }
    } catch (error) {
      if (sequence !== loadSequence.current) return;
      console.error("Public booking load failed:", error);
      toast.error(error?.message || "Buchungsseite konnte nicht geladen werden.");
      setOrg(null);
      setLocations([]);
      setServices([]);
    } finally {
      if (sequence === loadSequence.current) setLoading(false);
    }
  }, [activeLocationId, locationIdFromQuery, orgSlug, selectedLocationId]);

  // Muss vor allem anderen laufen: Die UTM-Parameter stehen nur beim
  // allerersten Aufruf in der URL. Sobald der Buchungsvorgang die Adresse
  // aendert oder ueber Stripe zurueckkommt, sind sie weg.
  useEffect(() => {
    erfasseHerkunft(window.location.search, document.referrer);
  }, []);

  useEffect(() => {
    if (!orgSlug) return;
    loadBookingPage();
    return () => { loadSequence.current += 1; };
  }, [loadBookingPage, orgSlug]);

  useEffect(() => {
    let active = true;
    const paymentStatus = searchParams.get("payment");
    const checkoutId = searchParams.get("checkout_id");
    const sessionId = searchParams.get("session_id");
    if (!paymentStatus || !checkoutId) {
      setPaymentNotice(null);
      return undefined;
    }

    (async () => {
      try {
        if (paymentStatus === "success") {
          const res = await publicAPI.finalizeCheckoutPayment(checkoutId, sessionId);
          const data = res?.data || {};
          if (!active) return;
          if (data.ok) {
            await emitTrackingEvent(data.tracking_event);
            if (!active) return;
            const redirectUrl = normalizePublicRedirectUrl(selectedLocation?.booking_success_redirect_url);
            if (redirectUrl) {
              window.location.href = redirectUrl;
              return;
            }
            const message = data.requires_confirmation
              ? "Zahlung erfolgreich. Deine Anfrage ist eingegangen und wird nun bestätigt."
              : "Zahlung erfolgreich. Dein Termin ist bestätigt.";
            setPaymentNotice({ type: "success", message });
            toast.success(message);
            return;
          }
          const fallback = data.message || "Der gewählte Termin war leider nicht mehr verfügbar. Die Zahlung wurde automatisch rückerstattet.";
          setPaymentNotice({ type: "cancelled", message: fallback });
          toast.error(fallback);
          return;
        }

        if (paymentStatus === "cancelled") {
          await publicAPI.cancelCheckoutPayment(checkoutId).catch(() => {});
          if (!active) return;
          const message = "Die Zahlung wurde nicht abgeschlossen. Es wurde kein Termin gebucht.";
          setPaymentNotice({ type: "cancelled", message });
          toast.message(message);
        }
      } catch (error) {
        if (!active) return;
        const message = error?.response?.data?.detail || "Der Zahlungsstatus konnte nicht verarbeitet werden.";
        setPaymentNotice({ type: "cancelled", message });
        toast.error(message);
      }
    })();

    return () => {
      active = false;
    };
  }, [searchParams, selectedLocation?.booking_success_redirect_url]);

  useEffect(() => {
    setSelectedCategoryId("all");
    setExpandedDescriptions({});
    setSelectedService(null);
    setBookingOpen(false);
    setOpenCategoryIds([]);
    setGoogleReviews(null);
  }, [activeLocationId]);

  useEffect(() => {
    let active = true;
    const studioSlug = selectedLocation?.slug;
    if (!studioSlug || embedded) {
      setGoogleReviews(null);
      return undefined;
    }

    publicStudioAPI.getGoogleReviews(studioSlug)
      .then((res) => {
        if (!active) return;
        if (res.data && res.data.reviews?.length) {
          setGoogleReviews(res.data);
        } else {
          setGoogleReviews(null);
        }
      })
      .catch(() => {
        if (active) setGoogleReviews(null);
      });

    return () => {
      active = false;
    };
  }, [embedded, selectedLocation?.slug]);

  const categories = useMemo(() => {
    const categoryMap = new Map();
    services.forEach((service) => {
      getServiceCategories(service).forEach((category) => {
        if (!categoryMap.has(category.id)) {
          categoryMap.set(category.id, category);
        }
      });
    });
    return Array.from(categoryMap.values()).sort((a, b) => (a.sort ?? 9999) - (b.sort ?? 9999));
  }, [services]);

  const serviceGroups = useMemo(
    () => buildGroupedServices(services, categories, selectedCategoryId),
    [categories, selectedCategoryId, services]
  );

  const returnPath = useMemo(() => {
    const nextParams = new URLSearchParams();
    if (embedded) nextParams.set("embedded", "1");
    if (activeLocationId) nextParams.set("location_id", activeLocationId);
    const query = nextParams.toString();
    if (selectedLocation?.slug) {
      return `/studio/${selectedLocation.slug}${query ? `?${query}` : ""}`;
    }
    return `/book/${orgSlug}${query ? `?${query}` : ""}`;
  }, [activeLocationId, embedded, orgSlug, selectedLocation]);

  const shouldChooseLocationFirst = !locationIdFromQuery && locations.length > 1 && !activeLocationId;
  const categoryDropdownMode = selectedLocation?.service_category_dropdown_mode || org?.service_category_dropdown_mode || "none";
  const shouldUseCategoryDropdowns = categoryDropdownMode !== "none" && selectedCategoryId === "all";
  const heroLogoUrl = selectedLocation?.logo_url || org?.logo_url || "";
  const heroLogoBackgroundColor = selectedLocation?.logo_background_color || "";
  const heroName = selectedLocation?.name || org?.name || "";
  const heroHeaderImageUrl = selectedLocation?.header_image_url || org?.header_image_url || "";
  const heroMobileHeaderImageUrl = selectedLocation?.mobile_header_image_url || org?.mobile_header_image_url || "";

  useEffect(() => {
    if (!shouldUseCategoryDropdowns || serviceGroups.length === 0) {
      setOpenCategoryIds([]);
      return;
    }
    if (categoryDropdownMode === "all_open") {
      setOpenCategoryIds(serviceGroups.map((group) => String(group.id)));
      return;
    }
    setOpenCategoryIds([String(serviceGroups[0].id)]);
  }, [categoryDropdownMode, serviceGroups, shouldUseCategoryDropdowns]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-black border-t-transparent" />
      </div>
    );
  }

  if (!org) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-[#86868B]">
        Standort nicht gefunden.
      </div>
    );
  }

  return (
    <div className={`${embedded ? "min-h-[100dvh] bg-transparent" : "min-h-screen bg-[#F5F5F7]"}`} data-testid="public-booking">
      <PracticeTracking orgSlug={orgSlug} />
      {!embedded && (
        <header className="bg-white border-b border-[#E5E5EA]">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4">
            <h1 className="text-xl font-bold text-[#1D1D1F]">{org.name}</h1>
            <p className="text-sm text-[#86868B]">Online-Terminbuchung</p>
          </div>
        </header>
      )}

      <div className={`${embedded ? "max-w-[560px]" : "max-w-3xl"} mx-auto px-4 sm:px-6 ${embedded ? "py-4" : "py-8"}`}>
        {embedded && !bookingOpen && (
          <section className="-mx-4 -mt-4 mb-6 overflow-hidden border-b border-[#E5E5EA] bg-black sm:-mx-6">
            {heroHeaderImageUrl ? (
              <div className="relative">
                <picture>
                  {heroMobileHeaderImageUrl ? <source media="(max-width: 767px)" srcSet={heroMobileHeaderImageUrl} /> : null}
                  <img src={heroHeaderImageUrl} alt={heroName} className="h-40 w-full object-cover sm:h-48" />
                </picture>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/20" />
                <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
                  {heroLogoUrl ? (
                    <img src={heroLogoUrl} alt={heroName} className={`mb-3 h-20 w-20 rounded-2xl object-contain p-[2px] shadow-lg ${heroLogoBackgroundColor?'':'bg-white/90'}`} style={heroLogoBackgroundColor?{backgroundColor:heroLogoBackgroundColor}:undefined} />
                  ) : null}
                  <div className="text-2xl font-bold tracking-tight text-white">{heroName}</div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center px-4 py-8 text-center">
                {heroLogoUrl ? (
                  <img src={heroLogoUrl} alt={heroName} className={`mb-3 h-20 w-20 rounded-2xl object-contain p-[2px] shadow-lg ${heroLogoBackgroundColor?'':'bg-white/90'}`} style={heroLogoBackgroundColor?{backgroundColor:heroLogoBackgroundColor}:undefined} />
                ) : null}
                <div className="text-2xl font-bold tracking-tight text-white">{heroName}</div>
              </div>
            )}
          </section>
        )}

        {paymentNotice ? (
          <div
            className={`mb-6 rounded-2xl p-4 text-sm ${
              paymentNotice.type === "success"
                ? "border border-[#34C759]/20 bg-[#34C759]/10 text-[#1D1D1F]"
                : "border border-[#F5D46B] bg-[#FFF8E1] text-[#6B5300]"
            }`}
          >
            {paymentNotice.message}
          </div>
        ) : null}

        {bookingOpen && selectedService ? (
          <BookingModal
            open={bookingOpen}
            embedded={true}
            onClose={() => {
              setBookingOpen(false);
              setSelectedService(null);
            }}
            orgSlug={orgSlug}
            org={org}
            service={selectedService}
            theme={theme}
            locationId={activeLocationId || null}
            locationName={selectedLocation?.name || org.name}
            returnPath={returnPath}
            successRedirectUrl={selectedLocation?.booking_success_redirect_url || ""}
          />
        ) : (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-[#1D1D1F]">
                {shouldChooseLocationFirst ? "Standort auswählen" : "Leistungen auswählen"}
              </h2>
            </div>

            {shouldChooseLocationFirst ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {locations.map((location) => (
                  <button
                    key={location.id}
                    type="button"
                    onClick={() => {
                      if (!embedded && location.slug) {
                        navigate(`/studio/${location.slug}`);
                        return;
                      }
                      setSelectedLocationId(String(location.id));
                    }}
                    className="overflow-hidden rounded-2xl bg-white text-left shadow-sm transition hover:shadow-md"
                  >
                    <div className="relative h-40 bg-[#F5F5F7]">
                      {location.header_image_url ? (
                        <img src={location.header_image_url} alt={location.name} className="h-full w-full object-cover" />
                      ) : null}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                      <div className="absolute inset-x-4 bottom-4 flex items-center gap-3">
                        {location.logo_url ? (
                          <img src={location.logo_url} alt={location.name} className={`h-14 w-14 rounded-2xl object-contain p-[2px] shadow-lg ${location.logo_background_color?'':'bg-white/90'}`} style={location.logo_background_color?{backgroundColor:location.logo_background_color}:undefined} />
                        ) : null}
                        <div>
                          <p className="text-lg font-semibold text-white">{location.name}</p>
                          {location.address ? <p className="text-xs text-white/80">{location.address}</p> : null}
                        </div>
                      </div>
                    </div>
                    <div className="p-4">
                      <span
                        className="inline-flex rounded-full px-4 py-2 text-sm font-medium text-white"
                        style={{ backgroundColor: theme.primary || theme.accent }}
                      >
                        Standort wählen
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <>
                {locations.length > 1 && !locationIdFromQuery ? (
                  <div className="flex items-center justify-between rounded-2xl border border-[#E5E5EA] bg-white p-3">
                    <div>
                      <p className="text-sm font-medium text-[#1D1D1F]">Gewählter Standort</p>
                      <p className="text-sm text-[#86868B]">{selectedLocation?.name || org?.name}</p>
                    </div>
                    <button
                      type="button"
                      className="text-sm font-medium text-[#1D1D1F] underline underline-offset-2"
                      onClick={() => setSelectedLocationId("")}
                    >
                      Standort wechseln
                    </button>
                  </div>
                ) : null}

                {categories.length > 0 ? (
                  <div className="bg-white rounded-2xl p-3 shadow-sm overflow-x-auto">
                    <div className="flex items-center gap-2 min-w-max">
                      <button
                        type="button"
                        onClick={() => setSelectedCategoryId("all")}
                        className={`px-4 py-2 rounded-full text-sm transition-colors ${
                          selectedCategoryId === "all"
                            ? "text-white"
                            : "bg-[#F5F5F7] text-[#1D1D1F] hover:bg-[#E5E5EA]"
                        }`}
                        style={selectedCategoryId === "all" ? { backgroundColor: theme.primary || theme.accent } : undefined}
                      >
                        Alle
                      </button>
                      {categories.map((category) => (
                        <button
                          key={category.id}
                          type="button"
                          onClick={() => setSelectedCategoryId(String(category.id))}
                          className={`px-4 py-2 rounded-full text-sm transition-colors ${
                            String(selectedCategoryId) === String(category.id)
                              ? "text-white"
                              : "bg-[#F5F5F7] text-[#1D1D1F] hover:bg-[#E5E5EA]"
                          }`}
                          style={String(selectedCategoryId) === String(category.id) ? { backgroundColor: theme.primary || theme.accent } : undefined}
                        >
                          {category.name}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}

                {serviceGroups.length === 0 ? (
                  <div className="bg-white rounded-2xl p-8 text-center text-[#86868B] shadow-sm">
                    Keine Services verfügbar.
                  </div>
                ) : (
                  <div className="space-y-8">
                    {serviceGroups.map((group) => (
                      <section key={group.id} className="space-y-3">
                        {(() => {
                          const isOpen = !shouldUseCategoryDropdowns || openCategoryIds.includes(String(group.id));
                          const header = group.bannerImageUrl ? (
                            <div className="relative w-full h-28 sm:h-36 rounded-2xl overflow-hidden border border-[#E5E5EA]">
                              <img src={group.bannerImageUrl} alt={group.name} className="w-full h-full object-cover" />
                              <div className="absolute inset-0 bg-black/35" />
                              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between gap-3">
                                <h3 className="text-white text-xl font-semibold">{group.name}</h3>
                                {shouldUseCategoryDropdowns ? (
                                  isOpen ? <ChevronUp className="h-5 w-5 text-white shrink-0" /> : <ChevronDown className="h-5 w-5 text-white shrink-0" />
                                ) : null}
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between gap-3 border-b border-[#E5E5EA] pb-4">
                              <div className="flex items-center gap-2 min-w-0">
                                <h3 className="text-xl font-semibold text-[#1D1D1F]">{group.name}</h3>
                                {shouldUseCategoryDropdowns ? (
                                  isOpen ? <ChevronUp className="h-5 w-5 text-[#1D1D1F] shrink-0" /> : <ChevronDown className="h-5 w-5 text-[#1D1D1F] shrink-0" />
                                ) : null}
                              </div>
                            </div>
                          );
                          if (!shouldUseCategoryDropdowns) return header;
                          return (
                            <button
                              type="button"
                              onClick={() => {
                                setOpenCategoryIds((current) =>
                                  current.includes(String(group.id))
                                    ? current.filter((id) => id !== String(group.id))
                                    : [...current, String(group.id)]
                                );
                              }}
                              className="w-full text-left"
                            >
                              {header}
                            </button>
                          );
                        })()}
                        {(!shouldUseCategoryDropdowns || openCategoryIds.includes(String(group.id))) ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {group.services.map((service) => (
                            <PublicBookingServiceCard
                              key={service.id}
                              service={service}
                              org={org}
                              theme={theme}
                              expandedDescriptions={expandedDescriptions}
                              setExpandedDescriptions={setExpandedDescriptions}
                              onSelect={(nextService) => {
                                setSelectedService(nextService);
                                setBookingOpen(true);
                              }}
                            />
                          ))}
                        </div>
                        ) : null}
                      </section>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {!embedded && googleReviews?.reviews?.length > 0 ? (
        <GoogleReviewsCarousel reviews={googleReviews} />
      ) : null}
    </div>
  );
}
